import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { GetAccess, GetRole, CheckEditAction, CheckDeleteAction } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import {
  FaCheckCircle,
  FaClipboardCheck,
  FaHourglassHalf,
  FaRegFileAlt,
  FaTimesCircle,
} from "react-icons/fa";

// Approval status icon renderer
const renderApprovalStatusIcon = (approvalStatus) => {
  const status = String(approvalStatus || "").trim().toLowerCase();
  if (status === "approved") {
    return <FaCheckCircle className="text-green-500 text-[18px] shrink-0" title="Approved" />;
  }
  if (status === "pending") {
    return <FaHourglassHalf className="text-yellow-500 text-[18px] shrink-0" title="Pending" />;
  }
  if (status === "rejected" || status === "declined") {
    return <FaTimesCircle className="text-red-500 text-[18px] shrink-0" title={approvalStatus} />;
  }
  if (status === "verified") {
    return <FaClipboardCheck className="text-purple-500 text-[18px] shrink-0" title="Verified" />;
  }
  if (status === "reviewed") {
    return <FaRegFileAlt className="text-indigo-500 text-[18px] shrink-0" title="Reviewed" />;
  }
  return null;
};

// GRN status badge color themes helper
const getGrnStatusTheme = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s.includes("approved") || s.includes("complete") || s.includes("verified") || s.includes("success") || s.includes("delivered")) {
    return "green";
  }
  if (s.includes("reject") || s.includes("cancel") || s.includes("fail")) {
    return "red";
  }
  if (s.includes("review") || s.includes("prepare") || s.includes("process")) {
    return "blue";
  }
  return "yellow";
};

const renderGrnStatus = (status) => {
  const theme = getGrnStatusTheme(status);

  const themes = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm",
    blue: "bg-blue-50 text-blue-700 border-blue-200/50 shadow-sm",
    yellow: "bg-amber-50 text-amber-700 border-amber-200/50 shadow-sm",
    red: "bg-rose-50 text-rose-700 border-rose-200/50 shadow-sm",
  };

  const colorClass = themes[theme] || themes.yellow;

  return (
    <span className={`inline-flex items-center text-center justify-center px-3.5 py-1 rounded-full text-[11px] font-bold border leading-snug tracking-wide max-w-[170px] ${colorClass}`}>
      {status || "N/A"}
    </span>
  );
};

// Date Renderer
const renderDate = (dateVal) => {
  if (!dateVal || dateVal === "None") return <span className="text-gray-400">-</span>;
  const formatted = moment(dateVal).format("DD-MMM-YYYY");

  return (
    <div className="flex items-center gap-1.5 text-gray-700 text-xs font-semibold">
      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <span>{formatted}</span>
    </div>
  );
};

// GRN Table Row Component
const GRNTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("grn_edit") || GetRole();
  const canDelete = GetAccess("grn_delete") || GetRole();

  const bgClass = "bg-white";
  const hoverClass = "hover:bg-white/80";

  const handleConfirmDelete = () => {
    handleDeleteData(`/grn/${row.id}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className={`${bgClass} ${hoverClass} transition-colors align-middle`}>
        {/* Checkbox */}
        <td className="p-4 text-center align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4 transition duration-150 ease-in-out cursor-pointer"
          />
        </td>

        {/* YPR */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/store/grn/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.prRef || "N/A"}
            </Link>
            <CopyButton text={row.prRef} />
          </div>
        </td>

        {/* GRN No */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/store/grn/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.ref || "N/A"}
            </Link>
            <CopyButton text={row.ref} />
          </div>
        </td>

        {/* PO No */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          {row.poRef ? (
            <div className="flex items-center text-xs font-semibold text-gray-700">
              <span>{row.poRef}</span>
              <CopyButton text={row.poRef} />
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* Supplier */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs font-semibold text-gray-700">
            {row.vendorName || "N/A"}
          </span>
        </td>

        {/* DN No */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          {row.dnRef ? (
            <div className="flex items-center text-xs font-semibold text-gray-700">
              <span>{row.dnRef}</span>
              <CopyButton text={row.dnRef} />
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* Busy GRN No */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          {row.busyGrnNo ? (
            <div className="flex items-center text-xs font-semibold text-gray-700">
              <span>{row.busyGrnNo}</span>
              <CopyButton text={row.busyGrnNo} />
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* Recvd Date */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          {renderDate(row.receivedDate)}
        </td>

        {/* Status */}
        <td className="p-4 text-center align-middle border-t border-b border-gray-200/60">
          <div className="flex justify-center">
            {renderGrnStatus(row.status)}
          </div>
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-center justify-center gap-1.5">
            {renderApprovalStatusIcon(row.approvalStatus)}
            {/* View */}
            <Link
              to={`/store/grn/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {CheckEditAction(row, "GRN", canEdit) ? (
              <Link
                to={`/store/grn/edit/${row.id}`}
                state={{ data: row, from: "/store/grn", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit GRN"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            ) : null}

            {/* Delete */}
            {CheckDeleteAction(row, canDelete) ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Delete GRN"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this GRN record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// GRN Card Component
const GRNCard = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("grn_edit") || GetRole();
  const canDelete = GetAccess("grn_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/grn/${row.id}`, setDeleteConfirmOpen);
  };

  const theme = getGrnStatusTheme(row.status);

  // Card themes classes
  const themeStyles = {
    green: {
      borderLeft: "border-l-4 border-l-emerald-500",
      iconBg: "bg-emerald-50 text-emerald-600",
    },
    blue: {
      borderLeft: "border-l-4 border-l-blue-500",
      iconBg: "bg-blue-50 text-blue-600",
    },
    yellow: {
      borderLeft: "border-l-4 border-l-amber-500",
      iconBg: "bg-amber-50 text-amber-600",
    },
    red: {
      borderLeft: "border-l-4 border-l-rose-500",
      iconBg: "bg-rose-50 text-rose-600",
    }
  };

  const styles = themeStyles[theme] || themeStyles.yellow;
  const borderLeftClass = styles.borderLeft;
  const iconBgClass = styles.iconBg;

  return (
    <div className={`relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-4 flex flex-col overflow-hidden ${borderLeftClass}`}>
      {/* Top Header Row of Card */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center sm:gap-4 w-full">
        <div className="flex items-center gap-6 flex-1 min-w-0">
          <div className="flex items-center gap-3 shrink-0">
            {/* Package / Inventory Icon */}
            <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center ${iconBgClass}`}>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Link
                  to={`/store/grn/view/${row.id}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="text-gray-800 font-bold hover:underline text-[14px]"
                >
                  {row.ref || "N/A"}
                </Link>
                <CopyButton text={row.ref} />
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-0.5">
                YPR: {row.prRef || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Right Side: Status Badge */}
        <div className="shrink-0 scale-95 origin-right">
          {renderGrnStatus(row.status)}
        </div>
      </div>

      {/* Middle Panel Grid */}
      <div className="rounded-xl py-2 flex justify-between lg:items-center items-start gap-1 mt-4 bg-gray-50/50 lg:px-4">
        {/* Supplier & PO */}
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Supplier / PO</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight truncate max-w-[130px]" title={row.vendorName}>
            {row.vendorName || "N/A"}
          </span>
          {row.poRef && (
            <span className="text-[10px] text-gray-500 font-semibold mt-0.5">
              PO: {row.poRef}
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-200" />

        {/* Delivery Note & Busy GRN */}
        <div className="flex flex-col gap-1 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">References</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            DN: {row.dnRef || "-"}
          </span>
          {row.busyGrnNo && (
            <span className="text-[10px] text-gray-500">Busy: {row.busyGrnNo}</span>
          )}
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-200" />

        {/* Received Date */}
        <div className="flex flex-col gap-1 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Recvd Date</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.receivedDate ? moment(row.receivedDate).format("DD-MMM-YYYY") : "--"}
          </span>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-4">
        {/* View Details & Approval Icon */}
        <div className="flex items-center gap-2.5">
          {renderApprovalStatusIcon(row.approvalStatus)}
          <Link
            to={`/store/grn/view/${row.id}`}
            state={{ data: row, pageIndex, searchQuery }}
            className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
            title="View details"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="hidden sm:inline">View Details</span>
          </Link>
        </div>

        {/* Edit & Delete */}
        <div className="flex items-center gap-1.5">
          {CheckEditAction(row, "GRN", canEdit) ? (
            <Link
              to={`/store/grn/edit/${row.id}`}
              state={{ data: row, from: "/store/grn", pageIndex, searchQuery }}
              className="border border-sky-100 text-[#0ea5e9] hover:bg-sky-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Edit"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </Link>
          ) : null}

          {CheckDeleteAction(row, canDelete) ? (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Delete"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this GRN record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main GRNTable Component
const GRNTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  listofNumbers = [],
  selectOneFunction,
  selectMultipleFunction,
  fullList = [],
  fetcher,
  dataLoading = false,
  showEdit = false,
  setShowEdit,
  viewMode = "table",
}) => {
  // exact column header names as requested
  const headers = [
    { title: "YPR" },
    { title: "GRN No." },
    { title: "PO No." },
    { title: "Supplier" },
    { title: "DN No." },
    { title: "Busy GRN No." },
    { title: "Recvd Date" },
    { title: "Status", align: "center" },
    { title: "Action", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <GRNTableRow
          row={row}
          isSelected={listofNumbers.includes(row.id)}
          onSelect={() => selectOneFunction(row.id)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      renderCard={(row, idx) => (
        <GRNCard
          row={row}
          isSelected={listofNumbers.includes(row.id)}
          onSelect={() => selectOneFunction(row.id)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      dataLoading={dataLoading}
      listofNumbers={listofNumbers}
      selectMultipleFunction={selectMultipleFunction}
      fullList={fullList}
      showEdit={showEdit}
      setShowEdit={setShowEdit}
      multiEditLabel="Multi Edit"
    />
  );
};

export default GRNTable;
